import { Component, computed, DestroyRef, inject, Input, OnInit, signal, viewChildren } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { selfPrintingPersonaSettingsType } from '@app/v2/event/enums/settings.enum'
import { SelfSettingsService } from '@app/v2/event/services/self-settings.service'
import { SnoballService } from '@app/v2/event/services/snoball.service'
import { EventApp, UpdateEventAppPayload } from '@app/v2/event/types/event-app.type'
import { selfPrintingPersonaSettings } from '@app/v2/event/types/settings.type'
import { SNOBALL_SLUG } from '@app/v2/event/utils/event-app.utils'
import { PersonaService } from '@app/v2/personas/services/persona.service'
import { Persona } from '@app/v2/personas/types/persona.type'
import { DeleteModalComponent } from '@app/v2/shared/components/upload/components/delete-modal/delete-modal.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { TranslateService } from '@ngx-translate/core'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { catchError, finalize, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs'
import { EventAppCardComponent } from './event-app-card/event-app-card.component'
import { EventAppsModalComponent } from './event-apps-modal/event-apps-modal.component'
import { HandshakeSetupModalComponent } from './handshake-setup-modal/handshake-setup-modal.component'
import { SnoballSetupModalComponent } from './snoball-setup-modal/snoball-setup-modal.component'

/** Longer than the default 3s so the activation confirmation can be read. */
const SNOBALL_ACTIVATION_SUCCESS_MS = 8000
const SNOBALL_PERSONAS_PAGE = 1
const SNOBALL_PERSONAS_PER_PAGE = 100

@Component({
  selector: 'app-event-apps',
  standalone: true,
  imports: [EventAppsModalComponent, EventAppCardComponent, NgxSkeletonLoaderModule, DeleteModalComponent, HandshakeSetupModalComponent, SnoballSetupModalComponent],
  providers: [SelfSettingsService, PersonaService, SnoballService],
  templateUrl: './event-apps.component.html'
})
export class EventAppsComponent implements OnInit {
  @Input() eventId!: number
  @Input() eventName = ''
  @Input() eventSlug = ''

  public readonly eventApps = signal<EventApp[]>([])
  public readonly isLoading = signal<boolean>(true)
  public readonly appToDelete = signal<EventApp | null>(null)
  public readonly deleteModalId = 'delete-event-app-modal'
  public readonly handshakeSetupApp = signal<EventApp | null>(null)
  public readonly handshakeSetupModalId = 'handshake-setup-modal'
  public readonly handshakePersonas = signal<selfPrintingPersonaSettings>([])
  public readonly handshakeLoadingAppId = signal<number | null>(null)
  public readonly snoballSetupApp = signal<EventApp | null>(null)
  public readonly snoballSetupModalId = 'snoball-setup-modal'
  public readonly snoballPersonas = signal<Persona[]>([])
  public readonly snoballLoadingAppId = signal<number | null>(null)
  public readonly snoballActivatingAppId = signal<number | null>(null)

  public readonly deleteConfirmMessage = computed(() => {
    const name = this.appToDelete()?.name ?? ''
    return `Are you sure you want to remove <strong>${name}</strong> from this event?`
  })

  private readonly _cardRefs = viewChildren(EventAppCardComponent)
  private readonly _modalService = inject(ModalService)
  private readonly _selfSettingsService = inject(SelfSettingsService)
  private readonly _personaService = inject(PersonaService)
  private readonly _authService = inject(AuthService)
  private readonly _alertService = inject(AlertService)
  private readonly _snoballService = inject(SnoballService)
  private readonly _translate = inject(TranslateService)
  private readonly _destroyRef = inject(DestroyRef)

  ngOnInit(): void {
    this._loadEventApps()
  }

  /**
   * Loads the event apps for the current event.
   */
  private _loadEventApps(): void {
    const orgId = this._authService.currentWorkspace?.id
    if (!orgId || !this.eventId) return
    this.isLoading.set(true)
    this._selfSettingsService
      .getEventApps(orgId, this.eventId)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (res) => this.eventApps.set(res.data),
        error: () => this.eventApps.set([])
      })
  }

  /**
   * Appends a new app to the local list.
   * @param {EventApp} app - The new app to add.
   */
  public onAppCreated(app: EventApp): void {
    this.eventApps.update((list) => {
      if (list.some((row) => row.id === app.id)) return list
      return [...list, app]
    })
  }

  /**
   * Opens the delete confirmation modal for the given app.
   * @param {EventApp} app - The app to remove.
   */
  public onAppRemoved(app: EventApp): void {
    this.appToDelete.set(app)
    if (!this._modalService.isModalOpen(this.deleteModalId)) {
      this._modalService.toggleModal(this.deleteModalId)
    }
  }

  /**
   * Whether the delete confirmation modal is currently open.
   * @returns {boolean}
   */
  public isDeleteModalOpen(): boolean {
    return this._modalService.isModalOpen(this.deleteModalId)
  }

  /**
   * Closes the delete confirmation modal and clears the pending app.
   */
  public closeDeleteModal(): void {
    if (this._modalService.isModalOpen(this.deleteModalId)) {
      this._modalService.toggleModal(this.deleteModalId)
    }
    this.appToDelete.set(null)
  }

  /**
   * Confirms deletion: calls `deleteEventApp` and removes the row on success.
   * A Snöball card also sets `options.snoball` to `{}` on every persona on the event first,
   * so a failed persona update leaves the card in place to retry.
   * The delete-modal closes itself on confirm; we toast and update the list here.
   */
  public onDeleteConfirmed(): void {
    const orgId = this._authService.currentWorkspace?.id
    const app = this.appToDelete()
    if (!orgId || !this.eventId || !app) return

    const eventId = this.eventId
    const clearPersonas$ = app.location_details.service.slug === SNOBALL_SLUG ? this._snoballService.clearEventPersonaSnoball(eventId) : of(null)

    clearPersonas$
      .pipe(
        switchMap(() => this._selfSettingsService.deleteEventApp(orgId, eventId, app.id)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (res) => {
          this.eventApps.update((list) => list.filter((eventApp) => eventApp.id !== app.id))
          this._alertService.alert(Alert.SUCCESS, res.message)
          this.appToDelete.set(null)
        },
        error: (err) => {
          this._alertService.alert(Alert.ERROR, err?.error?.message ?? err?.message ?? 'Failed to delete app')
          this.appToDelete.set(null)
        }
      })
  }

  /**
   * Persists a name/link change for an app. Optimistically swaps the row, syncs
   * with the server response, and reverts + toasts on error (the card's effect
   * re-applies the original values to its inputs).
   * @param {EventApp} app - The original app row.
   * @param {UpdateEventAppPayload} payload - The change to persist.
   */
  public onAppUpdated(app: EventApp, payload: UpdateEventAppPayload): void {
    this._updateEventAppObservable(app, payload).pipe(takeUntilDestroyed(this._destroyRef)).subscribe()
  }

  /**
   * Sends a single `updateEventApp` PATCH if the payload actually differs from
   * the current row. Optimistically swaps the row, syncs with the server
   * response on success, and reverts + toasts on error.
   * @param {EventApp} app - The original app row.
   * @param {UpdateEventAppPayload} payload - The change to persist.
   * @returns {Observable<void>} Completes when the PATCH settles (or immediately when nothing changed).
   */
  private _updateEventAppObservable(app: EventApp, payload: UpdateEventAppPayload): Observable<void> {
    const orgId = this._authService.currentWorkspace?.id
    if (!orgId || !this.eventId) return of(void 0)

    const nameChanged = payload.name !== app.name
    const linkChanged = (payload.location_details?.link ?? '') !== (app.location_details?.link ?? '')
    if (!nameChanged && !linkChanged) return of(void 0)

    const swap = (next: EventApp) => this.eventApps.update((list) => list.map((row) => (row.id === app.id ? next : row)))
    swap({ ...app, ...payload })

    return this._selfSettingsService.updateEventApp(orgId, this.eventId, app.id, payload).pipe(
      tap((res) => {
        swap(res.data)
        this._alertService.alert(Alert.SUCCESS, res.message)
      }),
      map(() => void 0),
      catchError((err) => {
        swap(app)
        this._alertService.alert(Alert.ERROR, err?.error?.message ?? err?.message ?? 'Failed to update app')
        return of(void 0)
      })
    )
  }

  /**
   * Pre-fetches the handshake personas for the given app and only opens the
   * setup modal once the response arrives. Prevents the modal from flashing
   * an in-modal loading state while the GET is in flight. Ignores extra clicks
   * while `handshakeLoadingAppId` holds a row id (fetch in progress).
   * @param {EventApp} app - The app whose handshake settings should be edited.
   */
  public onHandshakeSettings(app: EventApp): void {
    if (this.handshakeLoadingAppId()) return
    const orgId = this._authService.currentWorkspace?.id
    if (!orgId || !this.eventId) return

    this.handshakeLoadingAppId.set(app.id)
    this._selfSettingsService
      .getSelfSettings(orgId, this.eventId, { type: selfPrintingPersonaSettingsType.HANDSHAKE })
      .pipe(
        finalize(() => this.handshakeLoadingAppId.set(null)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (res) => {
          this.handshakePersonas.set(res.data ?? [])
          this.handshakeSetupApp.set(app)
          if (!this._modalService.isModalOpen(this.handshakeSetupModalId)) {
            this._modalService.toggleModal(this.handshakeSetupModalId)
          }
        },
        error: (err) => {
          this._alertService.alert(Alert.ERROR, err?.error?.message ?? err?.message ?? 'Failed to load handshake settings')
        }
      })
  }

  /**
   * Whether the handshake settings modal is currently open.
   * @returns {boolean}
   */
  public isHandshakeSetupModalOpen(): boolean {
    return this._modalService.isModalOpen(this.handshakeSetupModalId)
  }

  /**
   * Clears the selected handshake app and its preloaded personas when the
   * modal closes so the next open performs a fresh fetch.
   */
  public closeHandshakeSetupModal(): void {
    this.handshakeSetupApp.set(null)
    this.handshakePersonas.set([])
  }

  /**
   * Sends the Snöball activation Slack message from the app card.
   * @param {EventApp} app - The Snöball event app.
   */
  public onSnoballActivate(app: EventApp): void {
    if (this.snoballActivatingAppId()) return
    const organizationId = this._authService.currentWorkspace?.id
    if (!organizationId || !this.eventId) return

    this.snoballActivatingAppId.set(app.id)
    this._snoballService
      .requestActivation(organizationId, this.eventId, app, this.eventName, this.eventSlug)
      .pipe(
        finalize(() => this.snoballActivatingAppId.set(null)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (updated) => {
          this.onSnoballAppUpdated(updated)
          this._alertService.alert(Alert.SUCCESS, this._translate.instant('v2.eventApps.snoball.activationSuccess'), SNOBALL_ACTIVATION_SUCCESS_MS)
        },
        error: (err) => {
          this._alertService.alert(Alert.ERROR, err?.error?.message ?? err?.message ?? this._translate.instant('v2.eventApps.snoball.activationError'))
        }
      })
  }

  /**
   * Whether the Snöball configuration modal is currently open.
   * @returns {boolean}
   */
  public isSnoballSetupModalOpen(): boolean {
    return this._modalService.isModalOpen(this.snoballSetupModalId)
  }

  /**
   * Clears the selected Snöball app when the modal closes.
   */
  public closeSnoballSetupModal(): void {
    this.snoballSetupApp.set(null)
    this.snoballPersonas.set([])
  }

  /**
   * Replaces the Snöball row after an activation save.
   * @param {EventApp} app - Updated event app.
   */
  public onSnoballAppUpdated(app: EventApp): void {
    this.eventApps.update((list) => list.map((row) => (row.id === app.id ? app : row)))
    this.snoballSetupApp.set(app)
  }

  /**
   * Loads event personas then opens the Snöball configuration modal.
   * @param {EventApp} app - The Snöball event app.
   */
  public openSnoballSetup(app: EventApp): void {
    if (this.snoballLoadingAppId()) return
    const orgId = this._authService.currentWorkspace?.id
    if (!orgId || !this.eventId) return

    this.snoballLoadingAppId.set(app.id)
    this._personaService
      .getPersonas(orgId, this.eventId, SNOBALL_PERSONAS_PER_PAGE, SNOBALL_PERSONAS_PAGE)
      .pipe(
        finalize(() => this.snoballLoadingAppId.set(null)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (res) => {
          this.snoballPersonas.set(res.data ?? [])
          this.snoballSetupApp.set(app)
          if (!this._modalService.isModalOpen(this.snoballSetupModalId)) {
            this._modalService.toggleModal(this.snoballSetupModalId)
          }
        },
        error: (err) => {
          this._alertService.alert(Alert.ERROR, err?.error?.message ?? err?.message ?? 'Failed to load Snöball settings')
        }
      })
  }

  /**
   * Opens the add app modal.
   */
  public openAddAppModal(): void {
    this._modalService.toggleModal('event-apps-modal')
  }

  /**
   * Whether any card has unsaved edits. Consumed by the wizard close flow to
   * decide whether to show the unsaved-changes modal.
   * @returns {boolean}
   */
  public hasUnsavedChanges(): boolean {
    return this._cardRefs().some((card) => card.hasUnsavedChanges())
  }

  /**
   * Cancels any pending blur-triggered save on the cards so the wizard close
   * flow can prompt the user before any in-flight write happens.
   * @returns {void}
   */
  public cancelPendingBlurSave(): void {
    this._cardRefs().forEach((card) => card.cancelPendingBlurSave())
  }

  /**
   * Persists every card's unsaved payload in parallel and resolves when all
   * requests settle. Resolves immediately if nothing is unsaved. Called by the
   * wizard when the user picks "Save changes" in the unsaved-changes modal.
   * @returns {Observable<void>}
   */
  public saveUnsavedChanges(): Observable<void> {
    const requests = this._cardRefs()
      .map((card) => ({ app: card.eventApp(), payload: card.getUnsavedPayload() }))
      .filter((entry): entry is { app: EventApp; payload: UpdateEventAppPayload } => entry.payload !== null)
      .map(({ app, payload }) => this._updateEventAppObservable(app, payload))

    if (!requests.length) return of(void 0)
    return forkJoin(requests).pipe(map(() => void 0))
  }

  /**
   * Reverts every card's form back to the saved values. Used in route-based
   * wizard flows that explicitly discard pending edits.
   * @returns {void}
   */
  public revertToSaved(): void {
    this._cardRefs().forEach((card) => card.revertToSaved())
  }
}
